import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import {
	MAT_DATE_LOCALE,
	provideNativeDateAdapter,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepickerModule } from '@angular/material/timepicker';
import type {
	OperatorReservation,
	OperatorReservationInput,
	OperatorTable,
} from '@pressteaze/domain';
import { MessagesService } from '@pressteaze/ui';
import { debounceTime, merge } from 'rxjs';
import { OperatorStateService } from '../../../services/state-services/operator-state.service';
import { operatorPhoneValidator } from './reservation-phone-validator';
import {
	localDateKey,
	matchingOrNextTime,
	type OperatorTimeOption,
	toTimeOptions,
} from './reservation-time-options';

export type ReservationEditorMode = 'phone' | 'edit';

@Component({
	selector: 'app-operator-reservation-editor',
	imports: [ReactiveFormsModule, MatDatepickerModule, MatTimepickerModule],
	providers: [
		provideNativeDateAdapter(),
		{ provide: MAT_DATE_LOCALE, useValue: 'el-GR' },
	],
	templateUrl: './reservation-editor.component.html',
	styleUrl: './reservation-editor.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OperatorReservationEditorComponent implements OnInit {
	readonly operatorStateSrv = inject(OperatorStateService);
	private readonly messagesSrv = inject(MessagesService);
	readonly mode = input.required<ReservationEditorMode>();
	readonly reservation = input<OperatorReservation | null>(null);
	readonly closed = output<void>();
	readonly availableTables = signal<readonly OperatorTable[]>([]);
	readonly availabilityLoading = signal(true);
	readonly availabilityFailed = signal(false);
	readonly timeOptions = signal<readonly OperatorTimeOption[]>([]);
	readonly timeOptionsLoading = signal(true);
	readonly timeOptionsFailed = signal(false);
	readonly showNotes = signal(false);
	readonly validationAttempted = signal(false);
	readonly partySizeOptions = Array.from({ length: 12 }, (_, index) => index + 1);
	private availabilitySequence = 0;
	private timeOptionsSequence = 0;

	readonly bookingForm = new FormGroup({
		customerName: new FormControl('', {
			nonNullable: true,
			validators: [Validators.required, Validators.maxLength(120)],
		}),
		customerPhone: new FormControl('', {
			nonNullable: true,
			validators: [
				Validators.required,
				Validators.maxLength(30),
				operatorPhoneValidator,
			],
		}),
		partySize: new FormControl(2, {
			nonNullable: true,
			validators: [Validators.required, Validators.min(1), Validators.max(12)],
		}),
		startsDate: new FormControl<Date | null>(null, {
			validators: [Validators.required],
		}),
		startsTime: new FormControl<Date | null>(null, {
			validators: [Validators.required],
		}),
		areaId: new FormControl('', { nonNullable: true }),
		tableId: new FormControl({ value: '', disabled: true }, { nonNullable: true }),
		customerNotes: new FormControl('', {
			nonNullable: true,
			validators: [Validators.maxLength(1000)],
		}),
		operatorNotes: new FormControl('', {
			nonNullable: true,
			validators: [Validators.maxLength(1000)],
		}),
	});

	constructor() {
		this.bookingForm.controls.startsDate.valueChanges
			.pipe(takeUntilDestroyed())
			.subscribe(() => void this.loadTimeOptions());
		this.bookingForm.controls.areaId.valueChanges
			.pipe(takeUntilDestroyed())
			.subscribe(() => this.bookingForm.controls.tableId.setValue(''));
		merge(
			this.bookingForm.controls.partySize.valueChanges,
			this.bookingForm.controls.startsDate.valueChanges,
			this.bookingForm.controls.startsTime.valueChanges,
			this.bookingForm.controls.areaId.valueChanges,
		)
			.pipe(debounceTime(150), takeUntilDestroyed())
			.subscribe(() => void this.loadAvailableTables());
	}

	ngOnInit(): void {
		const reservation = this.reservation();
		const startsLocal = this.localInput(
			reservation ? new Date(reservation.startsAt) : new Date(),
		);
		const [startsDate, startsTime] = startsLocal.split('T');
		const [year, month, day] = startsDate.split('-').map(Number);
		const [hour, minute] = startsTime.split(':').map(Number);
		this.bookingForm.reset({
			customerName: reservation?.customerName ?? '',
			customerPhone: reservation?.customerPhone ?? '',
			partySize: reservation?.partySize ?? 2,
			startsDate: new Date(year, month - 1, day),
			startsTime: new Date(year, month - 1, day, hour, minute),
			areaId: reservation?.areaId ?? '',
			tableId: reservation?.tableId ?? '',
			customerNotes: reservation?.customerNotes ?? '',
			operatorNotes: reservation?.operatorNotes ?? '',
		});
		void this.loadTimeOptions();
		void this.loadAvailableTables();
	}

	async submit(): Promise<void> {
		if (this.operatorStateSrv.saving()) return;
		if (this.bookingForm.invalid) {
			this.validationAttempted.set(true);
			this.bookingForm.markAllAsTouched();
			if (this.bookingForm.controls.customerPhone.hasError('phone')) {
				this.messagesSrv.error(
					'Μη έγκυρος αριθμός τηλεφώνου',
					'Ελέγξτε τον αριθμό τηλεφώνου και δοκιμάστε ξανά.',
				);
				return;
			}
			this.messagesSrv.error(
				'Ελέγξτε τη φόρμα',
				'Συμπληρώστε τα υποχρεωτικά πεδία με έγκυρες τιμές.',
			);
			return;
		}
		const value = this.bookingForm.getRawValue();
		const reservation = this.reservation();
		const input: OperatorReservationInput = {
			source: 'phone',
			customerName: value.customerName || null,
			customerPhone: value.customerPhone || null,
			customerEmail: reservation?.customerEmail ?? null,
			partySize: value.partySize,
			startsLocal: this.startsLocalValue(),
			areaId: value.areaId || null,
			tableId: value.tableId || null,
			customerNotes: value.customerNotes || null,
			operatorNotes: value.operatorNotes || null,
		};
		const success = reservation
			? await this.operatorStateSrv.update({
				...input,
				reservationId: reservation.id,
				expectedVersion: reservation.version,
			})
			: await this.operatorStateSrv.create(input);
		if (success) this.closed.emit();
	}

	filteredTables() {
		return this.availableTables();
	}

	toggleNotes(): void {
		this.showNotes.update((visible) => !visible);
	}

	selectTable(tableId: string): void {
		const table = this.operatorStateSrv
			.context()
			?.tables.find((item) => item.id === tableId);
		if (table) {
			this.bookingForm.controls.areaId.setValue(table.areaId, {
				emitEvent: false,
			});
		}
	}

	getReservationSourceLabel(): string {
		if (this.reservation()?.source === 'online')
			return 'Online';
		if (this.reservation()?.source === 'phone')
			return 'Τηλεφωνική';
		return 'Walk-In';
	}

	private localInput(date: Date): string {
		const parts = new Intl.DateTimeFormat('en-CA', {
			timeZone: this.operatorStateSrv.context()?.timezone,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			hourCycle: 'h23',
		}).formatToParts(date);
		const value = Object.fromEntries(
			parts.map((part) => [part.type, part.value]),
		);
		return `${value['year']}-${value['month']}-${value['day']}T${value['hour']}:${value['minute']}`;
	}

	private async loadAvailableTables(): Promise<void> {
		const { partySize, areaId } = this.bookingForm.getRawValue();
		const startsLocal = this.startsLocalValue();
		const sequence = ++this.availabilitySequence;
		if (
			!Number.isInteger(partySize) ||
			partySize < 1 ||
			partySize > 50 ||
			!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(startsLocal)
		) {
			this.availableTables.set([]);
			this.availabilityLoading.set(false);
			this.bookingForm.controls.tableId.enable({ emitEvent: false });
			return;
		}

		this.availabilityLoading.set(true);
		this.availabilityFailed.set(false);
		this.bookingForm.controls.tableId.disable({ emitEvent: false });
		try {
			const tables = await this.operatorStateSrv.getAvailableTables(
				partySize,
				startsLocal,
				areaId || null,
				this.reservation()?.id ?? null,
			);
			if (sequence !== this.availabilitySequence) return;
			this.availableTables.set(tables);
			const selectedTableId = this.bookingForm.controls.tableId.value;
			if (
				selectedTableId &&
				!tables.some((table) => table.id === selectedTableId)
			) {
				this.bookingForm.controls.tableId.setValue('', { emitEvent: false });
			}
		} catch {
			if (sequence !== this.availabilitySequence) return;
			this.availableTables.set([]);
			this.availabilityFailed.set(true);
		} finally {
			if (sequence === this.availabilitySequence) {
				this.availabilityLoading.set(false);
				this.bookingForm.controls.tableId.enable({ emitEvent: false });
			}
		}
	}

	private async loadTimeOptions(): Promise<void> {
		const localDate = localDateKey(this.bookingForm.controls.startsDate.value);
		const sequence = ++this.timeOptionsSequence;
		if (!localDate) {
			this.timeOptions.set([]);
			this.timeOptionsLoading.set(false);
			return;
		}

		this.timeOptionsLoading.set(true);
		this.timeOptionsFailed.set(false);
		try {
			const startsLocal = await this.operatorStateSrv.getTimeOptions(localDate);
			if (sequence !== this.timeOptionsSequence) return;
			const options = toTimeOptions(startsLocal);
			this.timeOptions.set(options);
			if (!this.reservation()) {
				this.bookingForm.controls.startsTime.setValue(
					matchingOrNextTime(
						options,
						this.bookingForm.controls.startsTime.value,
					),
				);
			}
		} catch {
			if (sequence !== this.timeOptionsSequence) return;
			this.timeOptions.set([]);
			this.timeOptionsFailed.set(true);
		} finally {
			if (sequence === this.timeOptionsSequence) {
				this.timeOptionsLoading.set(false);
			}
		}
	}

	private startsLocalValue(): string {
		const date = this.bookingForm.controls.startsDate.value;
		const time = this.bookingForm.controls.startsTime.value;
		if (!date || !time) return '';
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		const hour = String(time.getHours()).padStart(2, '0');
		const minute = String(time.getMinutes()).padStart(2, '0');
		return `${year}-${month}-${day}T${hour}:${minute}`;
	}
}
